<template>
    <model-modal
        component="v-spa-right-panel-modal"
        :model-class="modelClass"
        modal-type="form"
        @opening="onOpening"
        @resetted="onReset"
        ref="modal"
        right-panel-width="600px"
        right-panel-width-xl="600px"
    >
        <template v-slot:default="slotProps">
            <div v-if="step == 'eventType'">
                <div class="mb-8">
                    <h2 class="mb-8 text-xl font-thin text-center text-gray-800">{{ $t('modals.calendar-event.event-type.title') }}</h2>
                    <h3 class="font-thin text-center text-gray-600">{{ $t('modals.calendar-event.event-type.subtitle') }}</h3>
                </div>
                <div class="flex items-stretch justify-center space-x-2">
                    <div class="flex items-center px-4 py-2 space-x-2 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-200" @click.prevent="setEventType('worksite')">
                        <img :src="`${$spa.TrustUpPro.url}/apps/worksite/icon.svg`" class="w-auto h-[20px]">
                        <span>
                            {{ $t('modals.calendar-event.event-type.worksite') }}
                        </span>
                    </div>
                    <div class="flex items-center px-4 py-2 space-x-2 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-200" @click.prevent="setEventType('appointment')">
                        <i class="far fa-handshake"></i> 
                        <span>
                            {{ $t('modals.calendar-event.event-type.appointment') }}
                        </span>
                    </div>
                </div>
            </div>
            <div v-if="step == 'form'">
                <h2 class="mb-8 text-xl font-thin text-center text-gray-800">
                    {{ model ? $t(`modals.calendar-event.${form.event_type}.edit_title`) : $t(`modals.calendar-event.${form.event_type}.create_title`) }}
                </h2>
                <form @submit.prevent="submit" class="mx-auto max-w-input">
                
                    <div class="w-full mb-6">
                        <v-spa-label id="calendar-event-form-user" :label="$t('form.labels.user')"></v-spa-label>
                        <select-user :as-field="true" :initial-value="form.user ? [form.user.id] : [$store.state.user.id]" @updated="setUserById"></select-user>
                    </div>
                
                    <div class="flex flex-col w-full mb-6 space-y-4">
                        <div class="w-full" v-if="displaySelectWorksite">
                            <v-spa-label id="activity-form-worksite" :label="$t('form.labels.worksite')"></v-spa-label>
                            <select-worksite :initial-value="form.worksite ? [form.worksite.uuid] : []" @updated="setWorksiteByUuid"></select-worksite>
                        </div>
                        
                        <div class="w-full" v-if="form.worksite">
                            <v-spa-label id="activity-form-worksite-phase" :label="$t('form.labels.worksite-phase')"></v-spa-label>
                            <select :class="$spa.css.form.select" id="activity-form-worksite_phase_uuid" v-model="form.worksite_phase" @change="setWorksitePhase(null, true)">
                                <option :value="null">{{ $t('form.worksite-phase.null') }}</option>
                                <option :value="phase" v-for="phase in form.worksite.phases" :key="phase.uuid">
                                    {{ phase.title }}
                                </option>
                            </select>
                        </div>
                    </div>
                    
                    <v-spa-input
                        id="calendar-event-form-title"
                        :form="form"
                        input-name="title"
                    />

                    <v-spa-address-input
                        id="calendar-event-form-address"
                        :form="form"
                        input-name="address"
                    />

                    <v-spa-date-picker
                        id="calendar-event-form-day"
                        :form="form"
                        input-name="day"
                        :popover="{ visibility: 'click' }"
                    />

                    <div class="flex items-center justify-between mb-6 space-x-4" @submit.prevent="setTimes">
                        <div class="flex items-center space-x-4">
                            <div>
                                <v-spa-label id="calendar-event-form-starts_at" :label="$t('form.labels.start_time')"></v-spa-label>
                                <input v-model="form.starts_at" type="text" id="calendar-event-form-starts_at" :class="$spa.css.form.input" v-mask="'##:##'" :placeholder="$t('form.times.placeholder')" @change="setTimesSilently" @blur="fixTimes">
                            </div>
                            <div>
                                <v-spa-label id="calendar-event-form-ends_at" :label="$t('form.labels.end_time')"></v-spa-label>
                                <input v-model="form.ends_at" type="text" id="calendar-event-form-ends_at" :class="$spa.css.form.input" v-mask="'##:##'" :placeholder="$t('form.times.placeholder')" @change="setTimesSilently" @blur="fixTimes">
                            </div>
                        </div>
                        <div>
                            <v-spa-label :class-name="`${$spa.css.form.label} text-center`" id="calendar-event-form-total_total" :label="$t('form.labels.total_time')"></v-spa-label>
                            <div class="h-[38px] flex justify-center">
                                <time-difference container-class-name="w-auto" ref="timeDifference" :start="startsAtDate" :end="endsAtDate"></time-difference>
                            </div>
                        </div>
                    </div>

                    <div class="mb-6">
                        <v-spa-checkbox
                            :form="form"
                            id="calendar-event-form-accounted_for_weekly_hours"
                            input-name="accounted_for_weekly_hours"
                            :label="$t('form.labels.accounted_for_weekly_hours')"
                        />
                        <v-spa-checkbox
                            :form="form"
                            id="calendar-event-form-timetracking_allowed"
                            input-name="timetracking_allowed"
                            :label="$t('form.labels.timetracking_allowed')"
                        />
                    </div>
                    
                    <v-spa-textarea
                        :form="form"
                        input-name="notes"
                    />
                    
                    <div class="flex justify-end">
                        <v-spa-loading-button :loading="form.isLoading()">
                            <i class="mr-2 far fa-check"></i>
                            {{ model ? $t(`modals.calendar-event.${form.event_type}.edit_button`) : $t(`modals.calendar-event.${form.event_type}.create_button`) }}
                        </v-spa-loading-button>
                    </div>
                </form>
            </div>
        </template>
    </model-modal>
</template>

<script>
    import Form from '~spa/classes/Form';
    import ModelModal from '@spa/apps/trustup-pro-base-js/components/Model/Modal';
    import CalendarEvent from '@spa/apps/trustup-pro-planning-js/models/CalendarEvent';
    import TimeActivityCalendarEventFormsMixin from '@spa/apps/trustup-pro-planning-js/mixins/TimeActivityCalendarEventFormsMixin';
    
    export default {
        mixins: [TimeActivityCalendarEventFormsMixin],
        components: {
            ModelModal
        },

        data() {
            return {
                modelClass: CalendarEvent,
                displaySelectWorksite: true,
                step: 'eventType',
                form: new Form({
                    eventType: null,
                    worksite: null,
                    worksite_phase: null,
                    trustup_io_task: null,
                    user: null,
                    title: null,
                    address: '',
                    starts_at: '08:00',
                    ends_at: '16:00',
                    timetracking_allowed: true,
                    accounted_for_weekly_hours: true,
                    day: null,
                    notes: ''
                })
            };
        },

        computed: {
            model()
            {
                return this.$refs.modal?.model || null;
            },
            button()
            {
                return this.model ? this.$t('modals.calendar-event.edit.button') : this.$t('modals.calendar-event.create.button');
            }
        },

        methods: {
            onOpening({form, model, source = 'grid'})
            {
                this.source = source;

                if ( this.model ) {
                    this.fillForm();
                    return;
                }

                this.form.day = form.start.format('YYYY-MM-DD');
                if ( form.start && form.start.format('HH:mm') !== '00:00' ) {
                    this.form.starts_at = form.start.format('HH:mm');
                    this.form.ends_at = form.end ? form.end.format('HH:mm') : form.start.add(1, 'hour').format('HH:mm');
                }
                this.form.event_type = form.eventType;
                
                let user = form.user || this.$store.state.user;
                this.form.user = user;
                this.form.title = user.options.time_activity_default_title;

                if ( form.step ) {
                    this.step = form.step;
                }

                if ( form.worksite ) {
                    this.form.worksite = form.worksite;
                    this.displaySelectWorksite = false;
                }
            },

            fillForm()
            {
                this.step = 'form';

                this.form.event_type = this.model.event_type || 'appointment';
                this.form.user = this.model.user;
                this.form.title = this.model.title;
                this.form.address = this.model.address || '';
                this.form.starts_at = this.model.getStartsAtTime();
                this.form.ends_at = this.model.getEndsAtTime();
                this.form.day = this.model.getStartsAt().format('YYYY-MM-DD');
                this.form.notes = this.model.notes;
                this.form.timetracking_allowed = this.model.timetracking_allowed;
                this.form.accounted_for_weekly_hours = this.model.accounted_for_weekly_hours;

                if ( this.model.worksite ) {
                    this.form.worksite = this.model.worksite;
                    this.form.event_type = 'worksite';
                }
            },

            setEventType(eventType)
            {
                this.form.event_type = eventType;

                if ( this.form.event_type == 'appointment' && this.source !== 'calendar' ) {
                    this.setTimes({
                        starts_at: this.form.starts_at,
                        ends_at: this.$moment(this.form.starts_at).add(1, 'hour').format('HH:mm')
                    });
                    this.form.title = this.$t('calendar-event.default_appointment_title');
                }

                this.step = 'form';
            },

            getFormData()
            {
                let data = {
                    event_type: this.form.event_type,
                    linked_to: this.getFormLinkedTo(),
                    user_id: this.form.user ? this.form.user.id : null,
                    title: this.form.title,
                    address: this.form.address,
                    starts_at: this.startsAtDate,
                    ends_at: this.endsAtDate,
                    timetracking_allowed: this.form.timetracking_allowed,
                    accounted_for_weekly_hours: this.form.accounted_for_weekly_hours,
                    notes: this.form.notes
                };

                return this.model ? {...{uuid: this.model.uuid}, ...{data: data}} : data;
            },

            getFormLinkedTo()
            {
                let linkedTo = [];

                if ( this.form.worksite ) {
                    linkedTo.push({
                        type: 'worksite',
                        uuid: this.form.worksite.uuid
                    });
                }

                if ( this.form.worksite_phase ) {
                    linkedTo.push({
                        type: 'worksite-phase',
                        uuid: this.form.worksite_phase.uuid
                    });
                }
                
                return linkedTo.length > 0 ? linkedTo : null;
            },

            async submit()
            {
                if ( this.form.isLoading() ) {
                    return;
                }

                this.form.load();
                let method = this.model ? 'update' : 'store';
                
                try {
                    await window.Planning.endpoints.calendarEvent[method](this.getFormData());
                    this.$refs.modal.close();
                } catch (data) {
                    console.error(data);
                    if ( typeof data.errors !== "undefined" ) {
                        this.form.onFail({errors: data.errors});
                    }
                }
            },

            onFormChange()
            {
                return;
            },

            onReset()
            {
                this.form.reset();
                this.step = 'eventType';
                this.source = null;
                this.displaySelectWorksite = true;
            }
        },
        created()
        {
            this.form.day = window.moment().format('YYYY-MM-DD');
        }
    }
</script>